import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { CoreApiService } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import { DicomPeer } from '../../../shared/models/dicom.model';
import { AdminService } from '../../admin.service';

@Component({
  host: { 'test-id': 'add-dicom-peer-dialog' },
  selector: 'add-dicom-peer-dialog',
  styleUrls: ['./add-dicom-peer-dialog.component.scss'],
  templateUrl: './add-dicom-peer-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddDicomPeerDialog implements OnDestroy {
  private admin = inject(AdminService);
  private dialogRef = inject<DialogRef<DicomPeer, AddDicomPeerDialog>>(DialogRef);
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notification = inject(NotificationService);

  public connectionTestMsg: string;
  public connectionTestPass = false;
  public ranConnectionTest = false;
  public showSpinner = false;
  public form: FormGroup = new FormGroup({
    aet: new FormControl('', [Validators.required, Validators.maxLength(16)]),
    host: new FormControl('', [Validators.required]),
    port: new FormControl('', [Validators.required]),
  });

  private subscriptions = { messages: Subscription.EMPTY };

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  addPeer(): void {
    for (const control of Object.values(this.form.controls)) {
      control.markAsTouched();
    }

    this.admin.addDicomPeer(this.form.value).subscribe({
      next: (device) => {
        this.notification.success('dimse.peer.add.success');
        this.dialogRef.close(device);
      },
      error: (error) => {
        this.notification.error('dimse.peer.add.error');
        this.logger.error(error);
      },
    });
  }

  canTestConnection(): boolean {
    const { host, port } = this.form.controls;
    return host?.valid && port?.valid && !this.showSpinner;
  }

  testConnection(): void {
    const { host, port, aet } = this.form.controls;
    this.ranConnectionTest = false;
    this.showSpinner = true;
    this.coreApi.ioProxy.dimse.peers.test(host.value, port.value, aet.value).subscribe(
      (resp) => {
        if (resp.status === 'Successful') {
          this.connectionTestPass = true;
          this.connectionTestMsg = 'Connection test passed.';
        } else if (resp.status === 'Unsuccessful') {
          this.connectionTestPass = false;
          this.connectionTestMsg = resp.message;
        }
        this.ranConnectionTest = true;
        this.showSpinner = false;
      },
      (error) => {
        this.logger.error(error);
        this.connectionTestPass = false;
        this.connectionTestMsg = 'There was an error running connection test.';
        this.ranConnectionTest = true;
        this.showSpinner = false;
      },
    );
  }
}
